import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import { cx } from "../../utils/cx";
import styles from "./IconButton.module.scss";
import type { ButtonSize, ButtonVariant } from "../Button/Button";

export interface IconButtonProps
    extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
    /** Required for screen readers — icon-only buttons MUST be labelled. */
    "aria-label": string;
    /** Icon node (svg). Sized via CSS to follow `size`. */
    children: ReactNode;
    /** Visual hierarchy. Defaults to `tertiary` (most common for icon-only). */
    variant?: ButtonVariant;
    /** Control square size (sm/md/lg). Mirrors Button heights: 32 / 40 / 52. */
    size?: ButtonSize;
    loading?: boolean;
    /** If true the button becomes a circle. Otherwise inherits Button radius. */
    rounded?: boolean;
}

/**
 * Treez DS — IconButton (Wave 1).
 *
 * Square (or circular when `rounded`) variant of `Button` for icon-only
 * controls. Mirrors the same variants/sizes/states as Button so that
 * Button + IconButton stay visually consistent.
 *
 * Accessibility: `aria-label` is required.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    function IconButton(
        {
            variant = "tertiary",
            size = "md",
            loading = false,
            rounded = false,
            className,
            disabled,
            children,
            type = "button",
            ...rest
        },
        ref,
    ) {
        const isDisabled = disabled || loading;
        return (
            <button
                aria-busy={loading || undefined}
                aria-disabled={isDisabled || undefined}
                className={cx(
                    styles.root,
                    rounded && styles.rounded,
                    className,
                )}
                data-loading={loading || undefined}
                data-size={size}
                data-ui="icon-button"
                data-variant={variant}
                disabled={isDisabled}
                ref={ref}
                type={type}
                {...rest}
            >
                {loading ? (
                    <span aria-hidden className={styles.spinner} />
                ) : (
                    children
                )}
            </button>
        );
    },
);

IconButton.displayName = "IconButton";
